import { useControls } from 'leva';
import { Perf } from 'r3f-perf';
import { useRef, useEffect, useState, lazy, Suspense } from 'react';
import useStore from './stores/useStore.jsx';

// Lazy load scene components to optimize initial bundle size
const Room1 = lazy(() => import('./scenes/Room1.jsx'));
const Room2 = lazy(() => import('./scenes/Room2.jsx'));
const Room3 = lazy(() => import('./scenes/Room3.jsx'));
const Room3Low = lazy(() => import('./scenes/Room3Low.jsx'));
const Room2Legacy = lazy(() => import('./scenes/Room2Legacy.jsx'));
const LasercutDetailRoom = lazy(() => import('./scenes/LasercutDetailRoom.jsx'));
const TechTest = lazy(() => import('./scenes/TechTest.jsx'));

/**
 * Experience component manages the 3D scene switching and global 3D state.
 */
export default function Experience() {
  const room1 = useRef();
  const room2 = useRef();
  const room3 = useRef();
  const lasercutDetailRoom = useRef();
  const techTest = useRef();

  const { currentScene, setCurrentScene, setIsDragging } = useStore((state) => ({
    currentScene: state.currentScene,
    setCurrentScene: state.setCurrentScene,
    setIsDragging: state.setIsDragging
  }));

  // UI Controls for world settings
  const { monitoring, debug, switchCameraControl } = useControls('world', {
    monitoring: false,
    scenes: {
      label: 'Scenes',
      options: { Room1: 1, Room2: 2, Room3: 3, Room3Low: 7, lasercutDetailRoom: 4, TechTest: 5, Room2Legacy: 6 },
      default: 1,
      onChange: (slug, propName, options) => {
        if (!options.initial) {
          switchScenes(slug);
        }
      }
    },
    switchCameraControl: {
      label: 'Camera',
      options: { FirstPerson: 'player', Free: 'orbit' }
    },
    debug: false
  });

  // UI Controls for Gaussian Splat specific settings
  const {alphaTest, toneMapping, show3DScan } = useControls('Splats', {
    show3DScan: true,
    alphaTest: {
      value: 0,
      min: 0,
      max: 0.99,
      step: 0.01
    },
    toneMapping: false
    },
    { collapsed: true } );

  // Handle Leva dragging
  useEffect(() => {
    const handleLevaMouseDown = (e) => {
      // Check if the click is on a Leva draggable element (usually the title or a handle)
      let target = e.target;
      while (target) {
        if (target.className && typeof target.className === 'string' && target.className.includes('leva')) {
          // If it's a dragging handle or title bar, we might want to disable camera
          setIsDragging(true);
          break;
        }
        target = target.parentElement;
      }
    };

    const handleLevaMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousedown', handleLevaMouseDown);
    window.addEventListener('mouseup', handleLevaMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleLevaMouseDown);
      window.removeEventListener('mouseup', handleLevaMouseUp);
    };
  }, [setIsDragging]);

  // Temporary pause state to handle camera transitions smoothly
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    setPaused(true);
    const timer = setTimeout(() => {
      setPaused(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Helper to switch between scenes and reset cursor
  function switchScenes(sceneNumber) {
    // TODO: Stop any ongoing room audio or animation
    document.body.style.cursor = 'default';
    setCurrentScene(sceneNumber);
  }

  return (
    <>
      {monitoring && <Perf position="top-left" />}
      <directionalLight position={[1, 2, 3]} intensity={4.5} />
      <ambientLight intensity={1.5} />

      {/* Render the active scene based on currentScene state with Suspense for lazy loading */}
      <Suspense fallback={null}>
        {currentScene === 1 && (
          <Room1
            ref={room1}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            laserCutterClicked={() => switchScenes(4)}
            doorClicked={() => switchScenes(2)}
            paused={paused}
          />
        )}
        {currentScene === 2 && (
          <Room2
            ref={room2}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            laserCutterClicked={() => switchScenes(4)}
            door1Clicked={() => switchScenes(1)}
            door3Clicked={() => switchScenes(3)}
            paused={paused}
          />
        )}
        {currentScene === 3 && (
          <Room3
            ref={room3}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            laserCutterClicked={() => switchScenes(4)}
            doorClicked={() => switchScenes(2)}
            paused={paused}
          />
        )}
        {currentScene === 7 && (
          <Room3Low
            ref={room3}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            laserCutterClicked={() => switchScenes(4)}
            doorClicked={() => switchScenes(2)}
            paused={paused}
          />
        )}
        {currentScene === 4 && (
          <LasercutDetailRoom
            ref={lasercutDetailRoom}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            onReturnClick={() => switchScenes(1)}
            paused={paused}
          />
        )}
        {currentScene === 5 && (
          <TechTest
            ref={techTest}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            paused={paused}
          />
        )}
        {currentScene === 6 && (
          <Room2Legacy
            ref={room2}
            debug={debug}
            alphaTest={alphaTest}
            toneMapping={toneMapping}
            show3DScan={show3DScan}
            cameraMode={switchCameraControl}
            laserCutterClicked={() => switchScenes(4)}
            door1Clicked={() => switchScenes(1)}
            door3Clicked={() => switchScenes(3)}
            paused={paused}
          />
        )}
      </Suspense>
    </>
  );
}
